// Communication Monitor — sacred fraud-prevention dashboard.
//
// Surfaces fraud flags generated by the backend scanner whenever a
// platform message contains off-platform / leakage signals like "cash",
// "bank transfer", "off-platform", "side deal", etc. See
// /var/www/kitchenos-api/modules/communication_monitor.js for the rule set.
//
// Backend endpoints:
//   GET  /api/admin/communication-flags?status=&severity=&limit=&retailerId=
//   GET  /api/admin/communication-flags/stats
//   POST /api/admin/communication-flags/:id/review  { action, note }
//      action ∈ ('dismissed', 'escalated')
//
// Per Steve's working rules: this protects the 3.3% commission model.
// Only strengthen — never weaken.

import { ApiError } from "@tkos/api-client";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────

type Severity = "critical" | "high" | "medium";
type Status = "open" | "reviewed" | "all";
type ReviewAction = "dismissed" | "escalated";

interface FraudFlag {
  id: string;
  createdAt: string;
  messageId: string | null;
  threadId: string | null;
  retailerId: string | null;
  retailerName: string | null;
  retailerEmail: string | null;
  supplierId: string | null;
  supplierName: string | null;
  customerId: string | null;
  direction: string | null;
  channel: string | null;
  subject: string;
  bodyExcerpt: string;
  matchedKeywords: string[];
  severity: Severity;
  reviewed: boolean;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewAction: ReviewAction | null;
  reviewNote: string | null;
}

interface Stats {
  totalFlags: number;
  openFlags: number;
  reviewedFlags: number;
  openBySeverity: { critical: number; high: number; medium: number };
  last24hCount: number;
}

// ── Helpers ──────────────────────────────────────────────────────────

function esc(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function relTime(iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "—";
  const diff = Date.now() - ms;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

function severityBadge(sev: Severity): string {
  return `<span class="cm-sev cm-sev-${sev}">${esc(sev.toUpperCase())}</span>`;
}

function directionLabel(direction: string | null): string {
  if (!direction) return "—";
  if (direction === "retailer-to-supplier") return "Retailer → Supplier";
  if (direction === "supplier-to-retailer") return "Supplier → Retailer";
  if (direction === "retailer-to-customer") return "Retailer → Customer";
  if (direction === "customer-to-retailer") return "Customer → Retailer";
  return direction;
}

function partiesLabel(f: FraudFlag): string {
  const retailer = f.retailerName || f.retailerEmail || f.retailerId || "—";
  const counterparty =
    f.supplierName ||
    f.supplierId ||
    (f.customerId ? `Customer ${f.customerId.slice(0, 8)}` : null) ||
    "—";
  return `${esc(retailer)} <span class="cm-arrow">↔</span> ${esc(counterparty)}`;
}

// ── State ────────────────────────────────────────────────────────────

interface State {
  status: Status;
  severity: Severity | "any";
  flags: FraudFlag[];
  stats: Stats | null;
  loading: boolean;
  error: string | null;
  expandedId: string | null;
}

// ── Page ─────────────────────────────────────────────────────────────

const page: Page = {
  name: "communication-monitor",
  label: "Comm Monitor",
  icon: "🛡️",
  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const state: State = {
      status: "open",
      severity: "any",
      flags: [],
      stats: null,
      loading: true,
      error: null,
      expandedId: null,
    };

    container.innerHTML = `
      <section class="cm-page">
        <header class="cm-header">
          <div>
            <h1>Communication Monitor</h1>
            <p class="cm-subtitle">
              Real-time scan for off-platform payment / leakage signals
              across every platform message. Protects the commission model.
            </p>
          </div>
        </header>
        <div class="cm-stats" data-stats></div>
        <div class="cm-filters">
          <div class="cm-filter-group" role="group" aria-label="Status filter">
            <button class="cm-chip" data-status="open" aria-pressed="true">Open</button>
            <button class="cm-chip" data-status="reviewed" aria-pressed="false">Reviewed</button>
            <button class="cm-chip" data-status="all" aria-pressed="false">All</button>
          </div>
          <div class="cm-filter-group" role="group" aria-label="Severity filter">
            <button class="cm-chip cm-chip-sev" data-severity="any" aria-pressed="true">Any severity</button>
            <button class="cm-chip cm-chip-sev cm-chip-critical" data-severity="critical" aria-pressed="false">Critical</button>
            <button class="cm-chip cm-chip-sev cm-chip-high" data-severity="high" aria-pressed="false">High</button>
            <button class="cm-chip cm-chip-sev cm-chip-medium" data-severity="medium" aria-pressed="false">Medium</button>
          </div>
          <button class="cm-refresh" data-refresh>Refresh</button>
        </div>
        <div class="cm-body" data-body></div>
      </section>
    `;

    const statsEl = container.querySelector<HTMLElement>("[data-stats]")!;
    const bodyEl = container.querySelector<HTMLElement>("[data-body]")!;
    const refreshBtn = container.querySelector<HTMLButtonElement>("[data-refresh]")!;
    const statusChips = container.querySelectorAll<HTMLButtonElement>("[data-status]");
    const severityChips = container.querySelectorAll<HTMLButtonElement>("[data-severity]");

    function renderStats(): void {
      if (!state.stats) {
        statsEl.innerHTML = `<div class="cm-stat-skel">…</div>`;
        return;
      }
      const s = state.stats;
      statsEl.innerHTML = `
        <div class="cm-stat cm-stat-open">
          <div class="cm-stat-num">${s.openFlags}</div>
          <div class="cm-stat-label">Open</div>
        </div>
        <div class="cm-stat cm-stat-critical">
          <div class="cm-stat-num">${s.openBySeverity.critical}</div>
          <div class="cm-stat-label">Critical (open)</div>
        </div>
        <div class="cm-stat cm-stat-high">
          <div class="cm-stat-num">${s.openBySeverity.high}</div>
          <div class="cm-stat-label">High (open)</div>
        </div>
        <div class="cm-stat cm-stat-medium">
          <div class="cm-stat-num">${s.openBySeverity.medium}</div>
          <div class="cm-stat-label">Medium (open)</div>
        </div>
        <div class="cm-stat">
          <div class="cm-stat-num">${s.last24hCount}</div>
          <div class="cm-stat-label">Last 24 hours</div>
        </div>
        <div class="cm-stat">
          <div class="cm-stat-num">${s.totalFlags}</div>
          <div class="cm-stat-label">Total flagged ever</div>
        </div>
      `;
    }

    function renderFlagRow(f: FraudFlag): string {
      const expanded = state.expandedId === f.id;
      const reviewedTag = f.reviewed
        ? `<span class="cm-reviewed-tag cm-reviewed-${f.reviewAction || "x"}">${esc((f.reviewAction || "reviewed").toUpperCase())}</span>`
        : "";
      const kw = f.matchedKeywords
        .map((k) => `<span class="cm-kw">${esc(k)}</span>`)
        .join(" ");
      const meta = [
        directionLabel(f.direction),
        f.channel ? `via ${esc(f.channel)}` : "",
        f.subject ? `“${esc(f.subject)}”` : "",
      ]
        .filter(Boolean)
        .join(" · ");
      return `
        <article class="cm-flag ${expanded ? "is-expanded" : ""} ${f.reviewed ? "is-reviewed" : ""}" data-flag-id="${esc(f.id)}">
          <header class="cm-flag-head" data-toggle="${esc(f.id)}">
            <div class="cm-flag-left">
              ${severityBadge(f.severity)}
              <span class="cm-flag-when">${esc(relTime(f.createdAt))}</span>
              <span class="cm-flag-parties">${partiesLabel(f)}</span>
              ${reviewedTag}
            </div>
            <div class="cm-flag-kw">${kw}</div>
          </header>
          <div class="cm-flag-meta">${meta}</div>
          ${
            expanded
              ? `
            <div class="cm-flag-body">
              <pre class="cm-flag-excerpt">${esc(f.bodyExcerpt)}</pre>
              ${
                !f.reviewed
                  ? `
                <div class="cm-flag-actions">
                  <input type="text" class="cm-note" data-note placeholder="Optional review note" maxlength="200" />
                  <button class="cm-btn cm-btn-dismiss" data-action="dismissed" data-id="${esc(f.id)}">Dismiss</button>
                  <button class="cm-btn cm-btn-escalate" data-action="escalated" data-id="${esc(f.id)}">Escalate</button>
                </div>
              `
                  : `
                <div class="cm-flag-review">
                  Reviewed by <strong>${esc(f.reviewedBy || "admin")}</strong>
                  ${esc(relTime(f.reviewedAt))}
                  ${f.reviewNote ? `· note: <em>${esc(f.reviewNote)}</em>` : ""}
                </div>
              `
              }
            </div>
          `
              : ""
          }
        </article>
      `;
    }

    function renderBody(): void {
      if (state.loading && state.flags.length === 0) {
        bodyEl.innerHTML = `<div class="cm-empty">Loading…</div>`;
        return;
      }
      if (state.error) {
        bodyEl.innerHTML = `<div class="cm-empty cm-error">Couldn't load flags: ${esc(state.error)}</div>`;
        return;
      }
      if (state.flags.length === 0) {
        bodyEl.innerHTML = `
          <div class="cm-empty cm-empty-ok">
            <div class="cm-empty-icon">✓</div>
            <div>No flags match this filter. The commission model is safe.</div>
          </div>
        `;
        return;
      }
      bodyEl.innerHTML = state.flags.map(renderFlagRow).join("");
    }

    async function loadStats(): Promise<void> {
      try {
        const r = await ctx.api<{ ok: boolean; stats: Stats }>("/admin/communication-flags/stats");
        if (r && r.ok && r.stats) {
          state.stats = r.stats;
        }
      } catch (err) {
        // Stats failure is non-fatal; the body shows the same data.
        if (err instanceof ApiError && err.status === 401) {
          ctx.showToast("Admin session expired — please log in again.", "error");
        }
      }
      renderStats();
    }

    async function loadFlags(): Promise<void> {
      state.loading = true;
      state.error = null;
      renderBody();
      try {
        const qs = new URLSearchParams();
        qs.set("status", state.status);
        if (state.severity !== "any") qs.set("severity", state.severity);
        qs.set("limit", "200");
        const r = await ctx.api<{ ok: boolean; flags: FraudFlag[] }>(
          `/admin/communication-flags?${qs.toString()}`,
        );
        if (r && r.ok && Array.isArray(r.flags)) {
          state.flags = r.flags;
        } else {
          state.flags = [];
        }
      } catch (err) {
        state.flags = [];
        state.error = err instanceof Error ? err.message : "Unknown error";
      } finally {
        state.loading = false;
        renderBody();
      }
    }

    async function refreshAll(): Promise<void> {
      await Promise.all([loadStats(), loadFlags()]);
    }

    function setStatusFilter(next: Status): void {
      state.status = next;
      statusChips.forEach((b) => {
        const on = b.dataset.status === next;
        b.setAttribute("aria-pressed", String(on));
        b.classList.toggle("is-active", on);
      });
      loadFlags();
    }

    function setSeverityFilter(next: Severity | "any"): void {
      state.severity = next;
      severityChips.forEach((b) => {
        const on = b.dataset.severity === next;
        b.setAttribute("aria-pressed", String(on));
        b.classList.toggle("is-active", on);
      });
      loadFlags();
    }

    async function reviewFlag(id: string, action: ReviewAction, note: string): Promise<void> {
      try {
        await ctx.api<{ ok: boolean; flag: FraudFlag }>(
          `/admin/communication-flags/${encodeURIComponent(id)}/review`,
          { method: "POST", body: JSON.stringify({ action, note }) },
        );
        ctx.showToast(
          action === "dismissed" ? "Flag dismissed." : "Flag escalated.",
          "success",
        );
        state.expandedId = null;
        await refreshAll();
      } catch (err) {
        ctx.showToast(
          `Review failed: ${err instanceof Error ? err.message : "Unknown error"}`,
          "error",
        );
      }
    }

    // ── Events ──────────────────────────────────────────────────────
    function onClick(e: Event): void {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const toggle = t.closest<HTMLElement>("[data-toggle]");
      if (toggle) {
        const id = toggle.dataset.toggle!;
        state.expandedId = state.expandedId === id ? null : id;
        renderBody();
        return;
      }
      const action = t.closest<HTMLButtonElement>("[data-action]");
      if (action) {
        const id = action.dataset.id!;
        const noteEl = action
          .closest<HTMLElement>(".cm-flag-actions")
          ?.querySelector<HTMLInputElement>("[data-note]");
        const note = (noteEl?.value || "").trim();
        const act = action.dataset.action as ReviewAction;
        void reviewFlag(id, act, note);
        return;
      }
    }
    bodyEl.addEventListener("click", onClick);

    statusChips.forEach((b) =>
      b.addEventListener("click", () => setStatusFilter(b.dataset.status as Status)),
    );
    severityChips.forEach((b) =>
      b.addEventListener("click", () =>
        setSeverityFilter(b.dataset.severity as Severity | "any"),
      ),
    );
    refreshBtn.addEventListener("click", () => void refreshAll());

    // Initial state highlighting
    statusChips.forEach((b) => b.classList.toggle("is-active", b.dataset.status === "open"));
    severityChips.forEach((b) =>
      b.classList.toggle("is-active", b.dataset.severity === "any"),
    );

    // Periodic refresh (every 30s)
    const tick = window.setInterval(() => {
      if (!document.hidden) void refreshAll();
    }, 30_000);

    void refreshAll();

    return () => {
      window.clearInterval(tick);
      bodyEl.removeEventListener("click", onClick);
    };
  },
};

registerPage(page);
